import { useDropzone } from 'react-dropzone';
import { useState, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemButton from '@mui/material/ListItemButton';

import { toast } from 'src/components/snackbar';
import { MultiFilePreview } from 'src/components/upload';
import { Iconify as IconifyComponent } from 'src/components/iconify';

import { useAuthContext } from 'src/auth/hooks';

import { MAX_ATTACHMENTS, MAX_ATTACHMENT_SIZE, ACCEPTED_ATTACHMENT_TYPES } from '../utils/attachments';

// ----------------------------------------------------------------------
/** @type {any} */
const Iconify = IconifyComponent;

/**
 * @param {{ disabled?: boolean, autoFocus?: boolean, onSend?: (text: string, files?: File[]) => void, onStop?: () => void, sending?: boolean, blocked?: boolean, blockedLabel?: string, sendingLabel?: string }} props
 */
export function ChatMessageInput({
  disabled,
  autoFocus,
  onSend = undefined,
  onStop = undefined,
  sending = false,
  blocked = false,
  blockedLabel = 'Please wait...',
  sendingLabel = '',
}) {
  const { user } = useAuthContext();
  const attachmentsEnabled = user?.features?.attachments ?? false;

  const [message, setMessage] = useState('');
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [attachAnchor, setAttachAnchor] = useState(/** @type {HTMLElement | null} */ (null));

  const handleChangeMessage = useCallback((event) => {
    setMessage(event.target.value);
  }, []);

  const handleRemoveFile = useCallback((file) => {
    setFiles((prev) => prev.filter((item) => item !== file));
  }, []);

  const handleDrop = useCallback((accepted, rejected) => {
    if (rejected?.length) {
      rejected.forEach(({ file, errors }) => {
        const reason =
          errors?.[0]?.code === 'file-too-large'
            ? `is too large (max ${Math.round(MAX_ATTACHMENT_SIZE / (1024 * 1024))}MB)`
            : 'is not a supported file type';
        toast.error(`${file.name} ${reason}`);
      });
    }

    if (!accepted?.length) return;

    setFiles((prev) => {
      const next = [...prev, ...accepted];
      if (next.length > MAX_ATTACHMENTS) {
        toast.error(`You can attach up to ${MAX_ATTACHMENTS} files.`);
        return next.slice(0, MAX_ATTACHMENTS);
      }
      return next;
    });
  }, []);

  const isBusy = sending || blocked;

  const { getRootProps, getInputProps, open: openFileDialog, isDragActive } = useDropzone({
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: disabled || isBusy || !attachmentsEnabled,
    maxSize: MAX_ATTACHMENT_SIZE,
    accept: ACCEPTED_ATTACHMENT_TYPES,
    onDrop: handleDrop,
  });

  const handleSendMessage = useCallback(
    async (event) => {
      try {
        if (event.key === 'Enter') {
          if (isBusy) return;
          const trimmed = message.trim();
          if (!trimmed && !files.length) return;

          if (typeof onSend === 'function') {
            const attachedFiles = files;
            setMessage('');
            setFiles([]);
            await onSend(trimmed, attachedFiles);
          }
        }
      } catch (error) {
        console.error(error);
      }
    },
    [message, files, onSend, isBusy]
  );

  const handleOpenAttachMenu = useCallback((event) => {
    setAttachAnchor(event.currentTarget);
  }, []);

  const handleCloseAttachMenu = useCallback(() => {
    setAttachAnchor(null);
  }, []);

  const handlePickFromComputer = useCallback(() => {
    handleCloseAttachMenu();
    openFileDialog();
  }, [handleCloseAttachMenu, openFileDialog]);

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          py: 0.75,
          px: 1,
          borderRadius: 3,
          border: (theme) =>
            `1px solid ${isDragActive ? theme.vars.palette.primary.main : theme.vars.palette.divider}`,
          bgcolor: 'background.paper',
          boxShadow: (theme) => theme.shadows[6],
          transition: (theme) => theme.transitions.create(['border-color']),
          outline: 'none',
        }}
      >
        <input {...getInputProps()} />

        {!!files.length && (
          <MultiFilePreview
            thumbnail
            files={files}
            onRemove={handleRemoveFile}
            sx={{ px: 0.5, pt: 0.5, pb: 1 }}
          />
        )}

        <Stack direction="row" alignItems="center">
          {attachmentsEnabled && (
            <IconButton
              size="small"
              color="inherit"
              disabled={disabled || isBusy}
              onClick={handleOpenAttachMenu}
              title="Add files"
            >
              <Iconify icon="solar:paperclip-bold" width={20} />
            </IconButton>
          )}
          <InputBase
            name="chat-message"
            id="chat-message-input"
            value={message}
            onKeyUp={handleSendMessage}
            onChange={handleChangeMessage}
            placeholder={
              sending
                ? sendingLabel || 'oneVIEW Insights is thinking...'
                : blocked
                  ? blockedLabel
                  : 'Message oneVIEW Insights'
            }
            disabled={disabled || isBusy}
            autoFocus={autoFocus}
            autoComplete="off"
            sx={{
              px: 1,
              minHeight: 40,
              flex: 1,
              '& .MuiInputBase-input': {
                fontSize: '0.97rem',
                lineHeight: 1.5,
              },
            }}
          />
          <IconButton
            disabled={sending ? false : disabled || blocked}
            color="inherit"
            size="small"
            onClick={sending ? onStop : () => handleSendMessage({ key: 'Enter' })}
            sx={{
              mr: 0.75,
              ...(sending && { color: 'error.main' }),
              ...(!sending && { color: 'primary.main' }),
            }}
          >
            {sending ? (
              <Iconify icon="solar:stop-bold" width={18} />
            ) : (
              <Iconify icon="solar:arrow-up-bold" width={18} />
            )}
          </IconButton>
        </Stack>
      </Box>

      {attachmentsEnabled && (
        <Popover
          open={!!attachAnchor}
          anchorEl={attachAnchor}
          onClose={handleCloseAttachMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { minWidth: 260, borderRadius: 2 } } }}
        >
          <ListItemButton sx={{ py: 1.25, px: 2, gap: 1.5 }} onClick={handlePickFromComputer}>
            <Iconify icon="solar:paperclip-bold" width={20} />
            <ListItemText
              primary="Add files"
              secondary="Upload from computer"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
              secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
            />
          </ListItemButton>
        </Popover>
      )}
    </>
  );
}
